/**
 * Built-in moderation flags (high_risk, low_confidence, asked_human) are stored as
 * smallint columns on `messages` for easy SQL filtering. Custom per-agent flags stay in `custom_message_flags` JSON.
 *
 * Values: 0 = none, 1 = flagged (active), 2 = resolved. NULL = legacy row (use JSON / eval_items).
 */

import type { ModerationMessage } from "../types"

export const BUILTIN_FLAG_STATE = {
  NONE: 0,
  FLAGGED: 1,
  RESOLVED: 2,
} as const

export type BuiltinFlagStateNumeric = 0 | 1 | 2

export const BUILTIN_FLAG_IDS_LIST = ["high_risk", "low_confidence", "asked_human"] as const
export type BuiltinFlagIdStr = (typeof BUILTIN_FLAG_IDS_LIST)[number]

export const BUILTIN_FLAG_COLUMN: Record<BuiltinFlagIdStr, keyof Pick<ModerationMessage,
  "highrisk" | "lowconf" | "askhuman">> = {
  high_risk: "highrisk",
  low_confidence: "lowconf",
  asked_human: "askhuman",
}

export function isBuiltinFlagId(flagId: string): flagId is BuiltinFlagIdStr {
  return (BUILTIN_FLAG_IDS_LIST as readonly string[]).includes(flagId)
}

export function getMessageFlag(flags: FlagDetectedItem[] | null | undefined, flagId: string): FlagDetectedItem | undefined {
  return (flags || []).find(f => f.flagId === flagId)
}

/** `custom_message_flags` JSON should only contain custom flags; strip builtins for writes. */
export function getCustomMessageFlagsOnly(flags: FlagDetectedItem[] | null | undefined): FlagDetectedItem[] {
  return (flags || []).filter(f => !isBuiltinFlagId(f.flagId))
}

/** At least one built-in column is non-null — row uses the column model for builtins. */
export function hasBuiltinFlagColumnSignal(m: ModerationMessage): boolean {
  return m.highrisk != null || m.lowconf != null || m.askhuman != null
}

/** Effective UI state for a built-in flag: columns first, else legacy JSON on `custom_message_flags`. */
export function getEffectiveBuiltinFlagState(
  m: ModerationMessage,
  flagId: string
): { flagged: boolean; resolved: boolean } {
  if (isBuiltinFlagId(flagId)) {
    const col = BUILTIN_FLAG_COLUMN[flagId]
    const v = m[col]
    if (v !== null && v !== undefined) {
      if (v === BUILTIN_FLAG_STATE.FLAGGED) return { flagged: true, resolved: false }
      if (v === BUILTIN_FLAG_STATE.RESOLVED) return { flagged: false, resolved: true }
      return { flagged: false, resolved: false }
    }
  }
  const f = getMessageFlag(m.custom_message_flags, flagId)
  if (!f) return { flagged: false, resolved: false }
  if (f.status === "resolved") return { flagged: false, resolved: true }
  return { flagged: true, resolved: false }
}

export function incomingHasActiveBuiltinColumn(m: ModerationMessage): boolean {
  return [m.highrisk, m.lowconf, m.askhuman].some(v => v === BUILTIN_FLAG_STATE.FLAGGED)
}

/** Legacy JSON path for builtins when columns are all NULL. */
export function legacyBuiltinActiveFromJson(m: ModerationMessage): boolean {
  if (!m.custom_message_flags?.length) return false
  for (const id of BUILTIN_FLAG_IDS_LIST) {
    const f = getMessageFlag(m.custom_message_flags, id)
    if (f && f.status !== "resolved") return true
  }
  return false
}

export function toggleBuiltinColumnValue(
  flagId: string,
  m: ModerationMessage | undefined
): Partial<Pick<ModerationMessage, "highrisk" | "lowconf" | "askhuman">> {
  if (!isBuiltinFlagId(flagId) || !m) return {}
  const col = BUILTIN_FLAG_COLUMN[flagId]
  let cur: number | null | undefined = m[col] as number | null | undefined
  if (cur === null || cur === undefined) {
    const f = getMessageFlag(m.custom_message_flags, flagId)
    cur = f ? (f.status === "resolved" ? BUILTIN_FLAG_STATE.RESOLVED : BUILTIN_FLAG_STATE.FLAGGED) : BUILTIN_FLAG_STATE.NONE
  }
  const next = cur === BUILTIN_FLAG_STATE.NONE
    ? BUILTIN_FLAG_STATE.FLAGGED
    : cur === BUILTIN_FLAG_STATE.FLAGGED
      ? BUILTIN_FLAG_STATE.RESOLVED
      : BUILTIN_FLAG_STATE.FLAGGED
  return { [col]: next } as Partial<Pick<ModerationMessage, "highrisk" | "lowconf" | "askhuman">>
}

export function resolveAllBuiltinColumns(m: ModerationMessage | undefined): Partial<Pick<ModerationMessage, "highrisk" | "lowconf" | "askhuman">> {
  if (!m) return {}
  const out: Partial<Pick<ModerationMessage, "highrisk" | "lowconf" | "askhuman">> = {}
  for (const id of BUILTIN_FLAG_IDS_LIST) {
    const col = BUILTIN_FLAG_COLUMN[id]
    const v = m[col] as number | null | undefined
    if (v === BUILTIN_FLAG_STATE.FLAGGED) {
      (out as Record<string, number>)[col] = BUILTIN_FLAG_STATE.RESOLVED
    } else if (v === null || v === undefined) {
      const f = getMessageFlag(m.custom_message_flags, id)
      if (f && f.status !== "resolved") (out as Record<string, number>)[col] = BUILTIN_FLAG_STATE.RESOLVED
    }
  }
  return out
}
